import { cva } from 'class-variance-authority'
import * as React from 'react'
import { Slot } from 'radix-ui'

import { Skeleton } from '@ui/components/ui/skeleton'
import { cn } from '@ui/lib/utils'

export type MetricCardTone = 'neutral' | 'success' | 'warning' | 'error' | 'info'
export type MetricCardState = 'ready' | 'loading' | 'empty' | 'error'

export interface MetricCardProps extends React.HTMLAttributes<HTMLElement> {
  label: React.ReactNode
  value?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  trend?: React.ReactNode
  trendLabel?: string
  tone?: MetricCardTone
  state?: MetricCardState
  emptyText?: string
  errorText?: string
  asChild?: boolean
  children?: React.ReactElement
}

const metricCardVariants = cva(
  'flex h-full min-w-0 flex-col gap-4 rounded-xl border p-6 text-card-foreground shadow-sm',
  {
    variants: {
      tone: {
        neutral: 'bg-card',
        success: 'border-success/40 bg-success-surface',
        warning: 'border-warning/40 bg-warning-surface',
        error: 'border-destructive/40 bg-destructive-surface',
        info: 'border-info/40 bg-info-surface',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

const toneTextClasses: Record<MetricCardTone, string> = {
  neutral: 'text-foreground',
  success: 'text-success-strong',
  warning: 'text-warning-strong',
  error: 'text-destructive-strong',
  info: 'text-info-strong',
}

function MetricCardContent({
  label,
  value,
  description,
  icon,
  trend,
  trendLabel,
  tone,
  state,
  emptyText,
  errorText,
}: Required<Pick<MetricCardProps, 'tone' | 'state' | 'emptyText' | 'errorText'>> &
  Pick<
    MetricCardProps,
    'label' | 'value' | 'description' | 'icon' | 'trend' | 'trendLabel'
  >) {
  const accessibleIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ 'aria-hidden'?: boolean }>, {
        'aria-hidden': true,
      })
    : icon

  return (
    <>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <span className="min-w-0 text-sm font-medium text-muted-foreground">
          {label}
        </span>
        {icon ? (
          <span className={cn('shrink-0', toneTextClasses[tone])} aria-hidden="true">
            {accessibleIcon}
          </span>
        ) : null}
      </div>

      {state === 'loading' ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-4 w-32 max-w-full" />
        </div>
      ) : state === 'empty' ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : state === 'error' ? (
        <p className="text-sm font-medium text-destructive-strong">{errorText}</p>
      ) : (
        <div className="flex flex-1 flex-col justify-end gap-2">
          {value !== undefined && value !== null ? (
            <div className={cn('text-3xl font-semibold tabular-nums', toneTextClasses[tone])}>
              {value}
            </div>
          ) : null}
          {description ? (
            <div className="text-sm text-muted-foreground">{description}</div>
          ) : null}
          {trend ? (
            <div className="text-sm font-medium">
              {trendLabel ? <span className="sr-only">{trendLabel}: </span> : null}
              {trend}
            </div>
          ) : null}
        </div>
      )}
    </>
  )
}

export function MetricCard({
  label,
  value,
  description,
  icon,
  trend,
  trendLabel,
  tone = 'neutral',
  state = 'ready',
  emptyText = 'Noch keine Daten',
  errorText = 'Kennzahl nicht verfügbar',
  asChild = false,
  children,
  className,
  ...props
}: MetricCardProps) {
  const content = (
    <MetricCardContent
      label={label}
      value={value}
      description={description}
      icon={icon}
      trend={trend}
      trendLabel={trendLabel}
      tone={tone}
      state={state}
      emptyText={emptyText}
      errorText={errorText}
    />
  )
  const sharedProps = {
    ...props,
    'aria-busy': state === 'loading' ? (true as const) : undefined,
    'data-state': state,
    className: cn(
      metricCardVariants({ tone }),
      asChild &&
        'outline-none transition-colors hover:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring',
      className,
    ),
  }

  if (asChild) {
    if (!children) {
      throw new Error('MetricCard with asChild requires exactly one child element.')
    }
    const child = React.Children.only(children)
    return (
      <Slot.Root {...sharedProps}>
        <Slot.Slottable>{React.cloneElement(child, undefined, content)}</Slot.Slottable>
      </Slot.Root>
    )
  }

  return <article {...sharedProps}>{content}</article>
}

export function MetricCardGrid({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      {...props}
      className={cn(
        'grid auto-rows-fr gap-4 sm:grid-cols-2 xl:grid-cols-4 [&>*]:h-full',
        className,
      )}
    />
  )
}
